import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Storefront, FloppyDisk, Warning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Panel de contenido del sitio. Todo lo editable desde acá se persiste en
 * db.settings key="site_content" y se puede leer desde /api/site-content
 * para mostrarse en el landing.
 */
export const ContentPanel = () => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/site-content`, { withCredentials: true });
      setContent(data || {});
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setContent((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // limpiar campos internos
      const payload = { ...content };
      delete payload.key;
      delete payload._id;
      await axios.post(`${API_URL}/api/admin/site-content`, payload, { withCredentials: true });
      toast.success('Contenido guardado');
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error al guardar';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const setFaq = (i, k, v) => {
    const faq = [...(content.faq || [])];
    faq[i] = { ...faq[i], [k]: v };
    set('faq', faq);
  };
  const addFaq = () => set('faq', [...(content.faq || []), { question: '', answer: '' }]);
  const rmFaq = (i) => set('faq', (content.faq || []).filter((_, idx) => idx !== i));

  const setTestimonial = (i, k, v) => {
    const t = [...(content.testimonials || [])];
    t[i] = { ...t[i], [k]: v };
    set('testimonials', t);
  };
  const addTestimonial = () => set('testimonials', [...(content.testimonials || []), { name: '', text: '', platform: '' }]);
  const rmTestimonial = (i) => set('testimonials', (content.testimonials || []).filter((_, idx) => idx !== i));

  if (loading) return <div className="p-8 text-center text-sm text-muted-foreground">Cargando contenido...</div>;

  const social = content.social_links || {};
  const setSocial = (k, v) => set('social_links', { ...social, [k]: v });

  return (
    <div className="space-y-6" data-testid="content-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Storefront size={20} weight="duotone" className="text-primary" /> Contenido del sitio
        </h2>
        <Button onClick={save} disabled={saving} data-testid="content-save">
          <FloppyDisk size={16} className="mr-1.5" /> {saving ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </div>

      {/* Marca */}
      <Card className="p-5 border-border" data-testid="brand-card">
        <h3 className="font-semibold mb-3">Marca</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1"><Label>Nombre del sitio</Label><Input value={content.brand_name || ''} onChange={(e) => set('brand_name', e.target.value)} data-testid="brand-name" /></div>
          <div className="space-y-1"><Label>Logo URL</Label><Input value={content.logo_url || ''} onChange={(e) => set('logo_url', e.target.value)} data-testid="brand-logo" /></div>
          <div className="space-y-1"><Label>Favicon URL</Label><Input value={content.favicon_url || ''} onChange={(e) => set('favicon_url', e.target.value)} data-testid="brand-favicon" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Color primario</Label><Input value={content.primary_color || ''} onChange={(e) => set('primary_color', e.target.value)} placeholder="#00E676" data-testid="brand-primary" /></div>
            <div className="space-y-1"><Label>Color secundario</Label><Input value={content.secondary_color || ''} onChange={(e) => set('secondary_color', e.target.value)} placeholder="#3b82f6" data-testid="brand-secondary" /></div>
          </div>
        </div>
      </Card>

      {/* Hero */}
      <Card className="p-5 border-border" data-testid="hero-card">
        <h3 className="font-semibold mb-3">Hero principal</h3>
        <div className="space-y-3">
          <div className="space-y-1"><Label>Título</Label><Input value={content.hero_title || ''} onChange={(e) => set('hero_title', e.target.value)} data-testid="hero-title" /></div>
          <div className="space-y-1"><Label>Subtítulo</Label><Textarea rows={2} value={content.hero_subtitle || ''} onChange={(e) => set('hero_subtitle', e.target.value)} data-testid="hero-subtitle" /></div>
          <div className="space-y-1"><Label>Texto del botón CTA</Label><Input value={content.hero_cta || ''} onChange={(e) => set('hero_cta', e.target.value)} data-testid="hero-cta" /></div>
        </div>
      </Card>

      {/* FAQ */}
      <Card className="p-5 border-border" data-testid="faq-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Preguntas frecuentes</h3>
          <Button size="sm" variant="outline" onClick={addFaq} data-testid="faq-add">+ Agregar</Button>
        </div>
        <div className="space-y-3">
          {(content.faq || []).map((f, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-start" data-testid={`faq-item-${i}`}>
              <Input value={f.question || ''} onChange={(e) => setFaq(i, 'question', e.target.value)} placeholder="Pregunta" />
              <Textarea rows={2} value={f.answer || ''} onChange={(e) => setFaq(i, 'answer', e.target.value)} placeholder="Respuesta" />
              <Button size="sm" variant="outline" onClick={() => rmFaq(i)}>Quitar</Button>
            </div>
          ))}
          {(!content.faq || content.faq.length === 0) && <p className="text-xs text-muted-foreground">Sin preguntas cargadas.</p>}
        </div>
      </Card>

      {/* Testimonials */}
      <Card className="p-5 border-border" data-testid="testimonials-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Testimonios</h3>
          <Button size="sm" variant="outline" onClick={addTestimonial} data-testid="testimonials-add">+ Agregar</Button>
        </div>
        <div className="space-y-3">
          {(content.testimonials || []).map((t, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_1fr_auto] gap-2 items-start" data-testid={`testimonial-${i}`}>
              <Input value={t.name || ''} onChange={(e) => setTestimonial(i, 'name', e.target.value)} placeholder="Nombre" />
              <Textarea rows={2} value={t.text || ''} onChange={(e) => setTestimonial(i, 'text', e.target.value)} placeholder="Testimonio" />
              <Input value={t.platform || ''} onChange={(e) => setTestimonial(i, 'platform', e.target.value)} placeholder="Plataforma (Instagram, TikTok...)" />
              <Button size="sm" variant="outline" onClick={() => rmTestimonial(i)}>Quitar</Button>
            </div>
          ))}
          {(!content.testimonials || content.testimonials.length === 0) && <p className="text-xs text-muted-foreground">Sin testimonios cargados.</p>}
        </div>
      </Card>

      {/* Redes */}
      <Card className="p-5 border-border" data-testid="social-card">
        <h3 className="font-semibold mb-3">Redes sociales</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1"><Label>Instagram URL</Label><Input value={social.instagram || ''} onChange={(e) => setSocial('instagram', e.target.value)} /></div>
          <div className="space-y-1"><Label>Facebook URL</Label><Input value={social.facebook || ''} onChange={(e) => setSocial('facebook', e.target.value)} /></div>
          <div className="space-y-1"><Label>WhatsApp (número)</Label><Input value={social.whatsapp || ''} onChange={(e) => setSocial('whatsapp', e.target.value)} placeholder="541171740757" /></div>
          <div className="space-y-1"><Label>TikTok URL</Label><Input value={social.tiktok || ''} onChange={(e) => setSocial('tiktok', e.target.value)} /></div>
        </div>
      </Card>

      {/* SEO */}
      <Card className="p-5 border-border" data-testid="seo-card">
        <h3 className="font-semibold mb-3">SEO / Meta Tags</h3>
        <div className="space-y-3">
          <div className="space-y-1"><Label>Meta title</Label><Input value={content.seo_title || ''} onChange={(e) => set('seo_title', e.target.value)} data-testid="seo-title" /></div>
          <div className="space-y-1"><Label>Meta description</Label><Textarea rows={2} value={content.seo_description || ''} onChange={(e) => set('seo_description', e.target.value)} data-testid="seo-description" /></div>
          <div className="space-y-1"><Label>Meta keywords</Label><Input value={content.seo_keywords || ''} onChange={(e) => set('seo_keywords', e.target.value)} placeholder="seguidores, likes, tiktok..." data-testid="seo-keywords" /></div>
          <div className="space-y-1"><Label>Google Analytics ID</Label><Input value={content.google_analytics_id || ''} onChange={(e) => set('google_analytics_id', e.target.value)} placeholder="G-XXXXXXXXXX" data-testid="ga-id" /></div>
        </div>
      </Card>

      {/* Scripts custom */}
      <Card className="p-5 border-border border-yellow-500/30" data-testid="scripts-card">
        <h3 className="font-semibold mb-1 flex items-center gap-2 text-yellow-500">
          <Warning size={18} weight="fill" /> Scripts personalizados
        </h3>
        <p className="text-xs text-muted-foreground mb-3">
          HTML/JS que se inyecta en el sitio (tracking extra, chatbots, etc.).
          Los handlers inline (<span className="font-mono">onclick=…</span>) se remueven automáticamente por seguridad.
        </p>
        <Textarea
          rows={6}
          value={content.custom_scripts || ''}
          onChange={(e) => set('custom_scripts', e.target.value)}
          placeholder="<script>...</script>"
          className="font-mono text-xs"
          data-testid="scripts-textarea"
        />
      </Card>
    </div>
  );
};
